/*
 * Levitation — Custom Apple Music player (theme asset). HYBRID build.
 *
 * Two playback engines, one UI:
 *  - PREVIEW (default): 30s preview MP3s via one shared <audio>. No SDK, no auth.
 *    Track data fetched lazily (IntersectionObserver) from the Apple Music
 *    catalog API with the public developer JWT; cached in sessionStorage.
 *  - MUSICKIT (opt-in): full-length songs for signed-in Apple Music members.
 *    MusicKit JS v3 is lazy-loaded ONLY when the visitor clicks the upgrade
 *    control (preloaded on hover/focus so authorize() keeps the user gesture),
 *    or when a returning member's first-party breadcrumb is present.
 *    Any SDK/auth/playback failure silently falls back to previews.
 *
 * Scale/efficiency invariants:
 *  - MusicKit SDK + instance are page singletons (guarded by promises).
 *  - Only one thing sounds at a time, across engines and across players
 *    (window.__levActive + levClaimPlayback).
 *  - Preview path never regresses: it is both the default and the fallback.
 */
(function () {
  if (window.customElements && customElements.get('apple-music-player')) return;

  /* ---------------- shared preview audio ---------------- */
  var AUDIO = null;
  function sharedAudio() {
    if (!AUDIO) { AUDIO = new Audio(); AUDIO.preload = 'none'; }
    return AUDIO;
  }

  /* ---------------- MusicKit v3 singletons ---------------- */
  var MK_CDN = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
  var mkLoadPromise = null;
  var mkConfigPromise = null;

  function levLoadMusicKit() {
    if (window.MusicKit) return Promise.resolve(window.MusicKit);
    if (mkLoadPromise) return mkLoadPromise;
    mkLoadPromise = new Promise(function (resolve, reject) {
      function onReady() { resolve(window.MusicKit); }
      document.addEventListener('musickitloaded', onReady, { once: true });
      var s = document.createElement('script');
      s.src = MK_CDN;
      s.async = true;
      s.onerror = function () {
        document.removeEventListener('musickitloaded', onReady);
        mkLoadPromise = null; // allow retry on a later click
        reject(new Error('musickit-load-failed'));
      };
      document.head.appendChild(s);
      setTimeout(function () { if (window.MusicKit) resolve(window.MusicKit); }, 8000);
    });
    return mkLoadPromise;
  }

  function levConfigureMusicKit(developerToken, storefront) {
    if (mkConfigPromise) return mkConfigPromise;
    mkConfigPromise = levLoadMusicKit().then(function (MusicKit) {
      return Promise.resolve(MusicKit.configure({
        developerToken: developerToken,
        storefrontId: storefront || 'us',
        app: { name: 'Levitation', build: '1.0' }
      })).then(function (instance) {
        var mk = instance || MusicKit.getInstance();
        window.__levMK_instance = mk;
        return mk;
      });
    }).catch(function (e) {
      mkConfigPromise = null; // allow retry
      throw e;
    });
    return mkConfigPromise;
  }

  /* ---------------- cross-engine active-playback owner ---------------- */
  // window.__levActive = { player, engine: 'preview'|'musickit' }
  function levClaimPlayback(player, engine) {
    var prev = window.__levActive;
    if (prev && (prev.player !== player || prev.engine !== engine)) {
      try { prev.player._stopEngine(prev.engine); } catch (e) {}
    }
    window.__levActive = { player: player, engine: engine };
  }

  /* ---------------- helpers ---------------- */
  function fmt(sec) {
    sec = Math.max(0, Math.floor(sec || 0));
    return Math.floor(sec / 60) + ':' + ('0' + (sec % 60)).slice(-2);
  }
  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  var PLAY_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  var PAUSE_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';
  var BREADCRUMB = 'lev-am-authed';
  var CACHE_PREFIX = 'lev-am2:'; // v2: track objects now carry catalog song ids

  class AppleMusicPlayer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.album = this.getAttribute('data-album') || '';
      this.storefront = this.getAttribute('data-storefront') || 'us';
      this.token = this.getAttribute('data-token') || '';
      this.full = this.getAttribute('data-full-enabled') === '1';
      this.tracks = [];
      this.cur = -1;
      this.playing = false;
      this.engine = 'preview';
      this.mk = null;
      this.rows = [];
      this.el = {
        now: this.querySelector('[data-now]'),
        nowName: this.querySelector('[data-now-name]'),
        pbtn: this.querySelector('[data-pbtn]'),
        fill: this.querySelector('[data-fill]'),
        cur: this.querySelector('[data-cur]'),
        dur: this.querySelector('[data-dur]'),
        list: this.querySelector('[data-list]'),
        status: this.querySelector('[data-status]'),
        upgrade: this.querySelector('[data-upgrade]'),
        upNote: this.querySelector('[data-upgrade-note]')
      };
      var self = this;
      this._onEnd = function () { self.next(); };
      this._onTime = function () {
        var a = sharedAudio();
        if (self.playing && self.engine === 'preview' && a.duration) {
          self.el.fill.style.width = (a.currentTime / a.duration * 100) + '%';
          self.el.cur.textContent = fmt(a.currentTime);
          self.el.dur.textContent = fmt(a.duration);
        }
      };
      if (this.el.pbtn) this.el.pbtn.addEventListener('click', function () { self.toggle(); });

      // Info tooltip (ⓘ): hover/focus is CSS; click-toggle covers touch.
      var infowrap = this.querySelector('[data-infowrap]');
      var infoBtn = this.querySelector('[data-info]');
      if (infowrap && infoBtn) {
        infoBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          infowrap.classList.toggle('is-open');
        });
        // clicks inside the tooltip (e.g. the full-songs action) must not close it
        infowrap.addEventListener('click', function (e) { e.stopPropagation(); });
        document.addEventListener('click', function () { infowrap.classList.remove('is-open'); });
      }

      // Compact variant: "All tracks" disclosure toggles the collapsed tracklist.
      this.el.expand = this.querySelector('[data-expand]');
      if (this.el.expand) {
        this.el.expand.addEventListener('click', function () {
          var open = self.classList.toggle('amp--expanded');
          self.el.expand.setAttribute('aria-expanded', open ? 'true' : 'false');
          var lbl = self.el.expand.querySelector('[data-expand-label]');
          if (lbl) lbl.textContent = open ? 'Hide tracks' : 'All tracks';
        });
      }

      if (this.full && this.el.upgrade) {
        // The full-songs action lives inside the ⓘ tooltip. Preload the SDK as
        // soon as the visitor shows intent on the ⓘ (hover/focus/touch), so the
        // eventual authorize() click keeps its user gesture (popup not blocked).
        var preload = function () { levLoadMusicKit().catch(function () {}); };
        var intentEl = this.querySelector('[data-infowrap]') || this.el.upgrade;
        intentEl.addEventListener('mouseenter', preload, { once: true });
        intentEl.addEventListener('focusin', preload, { once: true });
        intentEl.addEventListener('touchstart', preload, { once: true, passive: true });
        this.el.upgrade.addEventListener('click', function (e) { e.stopPropagation(); self._upgradeClick(); });
        // Returning member: cheap first-party breadcrumb, only then touch the SDK.
        var crumb = null;
        try { crumb = localStorage.getItem(BREADCRUMB); } catch (e) {}
        if (crumb === '1') {
          levConfigureMusicKit(this.token, this.storefront).then(function (mk) {
            if (mk.isAuthorized) self._enableFullPlayback(mk);
          }).catch(function () {});
        }
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { io.disconnect(); self.load(); } });
        }, { rootMargin: '250px' });
        io.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      var a = window.__levActive;
      if (a && a.player === this) {
        try { this._stopEngine(a.engine); } catch (e) {}
        window.__levActive = null;
      }
      this._detachMkListeners();
    }

    /* ---------------- track data (unchanged preview path) ---------------- */
    load() {
      var self = this;
      if (this._loading || this.tracks.length) return;
      this._loading = true;
      if (!this.album || !this.token) return this.fail();
      var key = CACHE_PREFIX + this.storefront + ':' + this.album;
      try {
        var cached = sessionStorage.getItem(key);
        if (cached) return this.render(JSON.parse(cached));
      } catch (e) {}
      var url = 'https://api.music.apple.com/v1/catalog/' + encodeURIComponent(this.storefront) +
        '/albums/' + encodeURIComponent(this.album);
      fetch(url, { headers: { Authorization: 'Bearer ' + this.token } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (data) {
          var album = data && data.data && data.data[0];
          var rel = (album && album.relationships && album.relationships.tracks && album.relationships.tracks.data) || [];
          var tracks = rel.map(function (t) {
            var a = t.attributes || {};
            var prev = (a.previews && a.previews[0] && a.previews[0].url) || '';
            return { id: t.id || '', name: a.name || '', dur: Math.round((a.durationInMillis || 0) / 1000), preview: prev };
          }).filter(function (t) { return t.preview; });
          var payload = { tracks: tracks };
          try { sessionStorage.setItem(key, JSON.stringify(payload)); } catch (e) {}
          self.render(payload);
        })
        .catch(function () { self.fail(); });
    }

    fail() { this.setAttribute('hidden', ''); }

    render(payload) {
      var self = this;
      this.tracks = (payload && payload.tracks) || [];
      if (!this.tracks.length) return this.fail();
      if (this.el.status) this.el.status.remove();
      var html = '';
      this.tracks.forEach(function (t, i) {
        html += '<button type="button" class="amp__row" data-i="' + i + '">' +
          '<span class="amp__idx"><span class="amp__num">' + (i + 1) + '</span>' +
          '<span class="amp__eq"><i></i><i></i><i></i></span></span>' +
          '<span class="amp__nm">' + esc(t.name) + '</span>' +
          '<span class="amp__dur">' + fmt(t.dur) + '</span>' +
          '<span class="amp__rbtn" aria-hidden="true">' + PLAY_SVG + '</span></button>';
      });
      this.el.list.innerHTML = html;
      this.rows = Array.prototype.slice.call(this.el.list.querySelectorAll('.amp__row'));
      this.rows.forEach(function (r) {
        r.addEventListener('click', function () {
          var i = +r.getAttribute('data-i');
          // second click on the playing track = pause/resume (Rough Trade behavior)
          if (i === self.cur) self.toggle();
          else self.select(i, true);
        });
      });
      this.removeAttribute('hidden');
      this.select(0, false);
      if (this._autoplayOnRender) {
        this._autoplayOnRender = false;
        this.select(0, true); // deep link (#listen): cue and attempt playback
      }
    }

    select(i, autoplay) {
      if (i < 0 || i >= this.tracks.length) return;
      this.cur = i;
      this.rows.forEach(function (r, k) { r.classList.toggle('is-active', k === i); });
      this.el.nowName.textContent = this.tracks[i].name;
      this.el.fill.style.width = '0%';
      this.el.cur.textContent = '0:00';
      // Preview mode shows the 30s cap; full mode shows the real length.
      var d = this.tracks[i].dur || 30;
      this.el.dur.textContent = fmt(this.engine === 'preview' ? Math.min(30, d) : d);
      if (autoplay) this.play();
    }

    /* ---------------- engine routing ---------------- */
    play() {
      if (this.engine === 'musickit' && this.mk) {
        var self = this;
        this._playMusicKit().catch(function () { self._fallbackToPreview(true); });
      } else {
        this._playPreview();
      }
    }

    _playPreview() {
      var self = this;
      var audio = sharedAudio();
      var t = this.tracks[this.cur];
      if (!t) return;
      levClaimPlayback(this, 'preview');
      if (audio.src !== t.preview) audio.src = t.preview;
      audio.addEventListener('ended', this._onEnd);
      audio.addEventListener('timeupdate', this._onTime);
      var p = audio.play();
      // Autoplay can be blocked (#listen deep link on a fresh page load, before
      // any gesture on the page). Revert to cued/paused, and if this play came
      // from the deep link, ARM the player: the visitor's first tap or keypress
      // anywhere on the page starts the track (that gesture satisfies the
      // browser's autoplay policy). Feels like autoplay without fighting it.
      if (p && p.then) {
        p.then(function () { self._deepLinkPending = false; })
         .catch(function () {
            self.playing = false;
            self._paintPlaying(false);
            if (self._deepLinkPending) self._armOnGesture();
         });
      }
      this.playing = true;
      this._paintPlaying(true);
    }

    _armOnGesture() {
      var self = this;
      if (this._armed) return;
      this._armed = true;
      this.classList.add('amp--armed'); // pulses the play button (CSS)
      var fire = function (e) {
        document.removeEventListener('click', fire, true);
        document.removeEventListener('keydown', fire, true);
        self._armed = false;
        self._deepLinkPending = false;
        self.classList.remove('amp--armed');
        // If the gesture is on the player itself, let its own controls handle it.
        if (self.contains(e.target)) return;
        self.play();
      };
      document.addEventListener('click', fire, true);
      document.addEventListener('keydown', fire, true);
    }

    _playMusicKit() {
      var self = this;
      var t = this.tracks[this.cur];
      if (!t) return Promise.reject(new Error('no-track'));
      levClaimPlayback(this, 'musickit');
      this._attachMkListeners();
      // resume (don't restart) when the same track is just paused
      if (this._mkQueuedKey && this._mkQueuedKey === (t.id || this.cur) && this.mk.playbackState === 3) {
        return Promise.resolve(this.mk.play()).then(function () {
          self.playing = true;
          self._paintPlaying(true);
        });
      }
      this._mkQueuedKey = t.id || this.cur;
      var q = t.id
        ? { songs: [t.id], startPlaying: true }
        : { album: this.album, startWith: this.cur, startPlaying: true };
      return Promise.resolve(this.mk.setQueue(q)).then(function () {
        self.playing = true;
        self._paintPlaying(true);
      });
    }

    _stopEngine(engine) {
      if (engine === 'musickit') {
        if (this.mk) { try { this.mk.stop(); } catch (e) {} }
        this._detachMkListeners();
      } else {
        var audio = sharedAudio();
        try { audio.pause(); } catch (e) {}
        audio.removeEventListener('ended', this._onEnd);
        audio.removeEventListener('timeupdate', this._onTime);
      }
      this.playing = false;
      this._paintPlaying(false);
    }

    stop() { this._stopEngine(this.engine); }

    toggle() {
      if (this.playing) {
        if (this.engine === 'musickit' && this.mk) {
          try { this.mk.pause(); } catch (e) {}
          this.playing = false;
          this._paintPlaying(false);
        } else {
          this.stop();
        }
      } else {
        this.play();
      }
    }

    next() {
      if (this.cur < this.tracks.length - 1) this.select(this.cur + 1, true);
      else { this.stop(); this.select(0, false); }
    }

    _paintPlaying(on) {
      if (this.el.now) this.el.now.classList.toggle('is-playing', on);
      if (this.el.pbtn) {
        this.el.pbtn.innerHTML = on ? PAUSE_SVG : PLAY_SVG;
        this.el.pbtn.setAttribute('aria-label', on ? 'Pause' : 'Play');
      }
      // sync the per-row circular buttons
      var cur = this.cur;
      this.rows.forEach(function (r, k) {
        var isCur = k === cur;
        r.classList.toggle('is-playing', isCur && on);
        var b = r.querySelector('.amp__rbtn');
        if (b) b.innerHTML = (isCur && on) ? PAUSE_SVG : PLAY_SVG;
      });
    }

    /* ---------------- MusicKit upgrade flow ---------------- */
    _upgradeClick() {
      var self = this;
      this._setUpgradeState('authorizing');
      // Watchdog: authorize() can hang if the popup is blocked or abandoned.
      // Re-enable the control after 45s; if auth later resolves it still upgrades.
      clearTimeout(this._upWatch);
      this._upWatch = setTimeout(function () {
        if (self.engine !== 'musickit') self._setUpgradeState('idle');
      }, 45000);
      // configure (cached after first time) then authorize inside the gesture chain
      levConfigureMusicKit(this.token, this.storefront)
        .then(function (mk) {
          return Promise.resolve(mk.authorize()).then(function () { return mk; });
        })
        .then(function (mk) {
          if (!mk.isAuthorized) throw new Error('not-authorized');
          clearTimeout(self._upWatch);
          try { localStorage.setItem(BREADCRUMB, '1'); } catch (e) {}
          self._enableFullPlayback(mk);
        })
        .catch(function () {
          clearTimeout(self._upWatch);
          self._setUpgradeState('idle'); // silent fallback: previews keep working
        });
    }

    _enableFullPlayback(mk) {
      this.mk = mk;
      var wasPlaying = this.playing && this.engine === 'preview';
      if (wasPlaying) this._stopEngine('preview');
      this.engine = 'musickit';
      this._setUpgradeState('authorized');
      // refresh visible duration to full length
      if (this.cur >= 0 && this.tracks[this.cur]) {
        this.el.dur.textContent = fmt(this.tracks[this.cur].dur || 0);
      }
      if (wasPlaying) this.play(); // resume same track, now full-length (gesture-adjacent)
    }

    _fallbackToPreview(resume) {
      this._stopEngine('musickit');
      this.engine = 'preview';
      this._setUpgradeState('idle');
      try { localStorage.removeItem(BREADCRUMB); } catch (e) {}
      if (this.cur >= 0 && this.tracks[this.cur]) {
        this.el.dur.textContent = fmt(Math.min(30, this.tracks[this.cur].dur || 30));
      }
      if (resume) this._playPreview();
    }

    _setUpgradeState(state) {
      if (!this.el.upgrade) return;
      if (state === 'authorizing') {
        this.el.upgrade.disabled = true;
        this.el.upgrade.querySelector('[data-upgrade-label]').textContent = 'Connecting to Apple Music…';
      } else if (state === 'authorized') {
        this.el.upgrade.hidden = true;
        if (this.el.upNote) this.el.upNote.hidden = false;
      } else { // idle
        this.el.upgrade.disabled = false;
        this.el.upgrade.hidden = false;
        this.el.upgrade.querySelector('[data-upgrade-label]').textContent = 'Play full songs with Apple Music';
        if (this.el.upNote) this.el.upNote.hidden = true;
      }
    }

    /* ---------------- MusicKit event wiring ---------------- */
    _owns() {
      var a = window.__levActive;
      return !!(a && a.player === this && a.engine === 'musickit');
    }

    _attachMkListeners() {
      if (this._mkWired || !this.mk) return;
      this._mkWired = true;
      var self = this;
      this._mkTime = function () {
        if (!self._owns()) return;
        var cur = self.mk.currentPlaybackTime || 0;
        var dur = self.mk.currentPlaybackDuration || 0;
        if (dur > 0) {
          self.el.fill.style.width = (cur / dur * 100) + '%';
          self.el.cur.textContent = fmt(cur);
          self.el.dur.textContent = fmt(dur);
        }
      };
      this._mkState = function () {
        if (!self._owns()) return;
        var st = self.mk.playbackState;
        // PlaybackStates: playing=2, paused=3, stopped=4, ended=5, completed=10
        if (st === 2) { self.playing = true; self._paintPlaying(true); }
        else if (st === 3) { self.playing = false; self._paintPlaying(false); }
        else if (st === 5 || st === 10) { self.playing = false; self._paintPlaying(false); self.next(); }
      };
      this._mkError = function () {
        if (!self._owns()) return;
        self._fallbackToPreview(true);
      };
      this._mkAuth = function () {
        if (self.mk && !self.mk.isAuthorized) self._fallbackToPreview(false);
      };
      this.mk.addEventListener('playbackTimeDidChange', this._mkTime);
      this.mk.addEventListener('playbackStateDidChange', this._mkState);
      this.mk.addEventListener('mediaPlaybackError', this._mkError);
      this.mk.addEventListener('authorizationStatusDidChange', this._mkAuth);
    }

    _detachMkListeners() {
      if (!this._mkWired || !this.mk) return;
      this._mkWired = false;
      try {
        this.mk.removeEventListener('playbackTimeDidChange', this._mkTime);
        this.mk.removeEventListener('playbackStateDidChange', this._mkState);
        this.mk.removeEventListener('mediaPlaybackError', this._mkError);
        this.mk.removeEventListener('authorizationStatusDidChange', this._mkAuth);
      } catch (e) {}
    }
  }

  customElements.define('apple-music-player', AppleMusicPlayer);

  /* ---------------- #listen deep link ----------------
   * Any product URL with #listen (or an on-page <a href="#listen">) opens the
   * Listen experience: activates the sidebar Listen tab (or the compact player
   * when no tab exists), scrolls it into view, and cues + plays track 1.
   * On-page clicks carry a user gesture so audio starts immediately; external
   * arrivals may have autoplay blocked, in which case the track sits cued and
   * one tap plays (handled by the _playPreview catch).
   */
  function levListenDeepLink() {
    if ((location.hash || '').toLowerCase() !== '#listen') return;
    setTimeout(function () {
      var player = document.querySelector('apple-music-player.amp--tab') ||
                   document.querySelector('apple-music-player');
      if (!player || !player._init) return;
      if (player.classList.contains('amp--tab')) {
        var tabBtn = document.querySelector('[data-pst-tab="tracklist"]');
        if (tabBtn) tabBtn.click(); // the tabs script handles activation
        var box = player.closest('[data-pst-block]') || player;
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        player.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (player.classList.contains('amp--compact') && !player.classList.contains('amp--expanded')) {
          var ex = player.querySelector('[data-expand]');
          if (ex) ex.click();
        }
      }
      player._deepLinkPending = true; // if play() is blocked, arm first-gesture resume
      if (player.tracks && player.tracks.length) {
        player.select(0, true);
      } else {
        player._autoplayOnRender = true;
        try { player.load(); } catch (e) {}
      }
    }, 60);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', levListenDeepLink);
  } else {
    levListenDeepLink();
  }
  window.addEventListener('hashchange', levListenDeepLink);
})();
